/**
 * CommitmentsTracker (C-1f) — post-approval regulatory commitments.
 * Status KPI cards + filters + table + "Record commitment" dialog.
 * Route: /regulatory/commitments
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import {
  useCommitmentsQuery, useCreateCommitment, COMMITMENT_STATUS_LABEL, COMMITMENT_TYPE_LABEL, type NewCommitmentInput,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function statusClass(s: string) {
  if (s === 'FULFILLED') return styles.stageDone;
  if (s === 'CANCELLED') return styles.stageWarn;
  return styles.stageActive;
}

export function CommitmentsTracker() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const q = useCommitmentsQuery({ status: status || undefined, type: type || undefined });
  const create = useCreateCommitment();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewCommitmentInput>({ title: '', commitmentType: 'STABILITY_DATA', authority: 'SFDA' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;
  const today = Date.now();

  async function submit() {
    setErr(null);
    if (!form.title.trim()) { setErr('A title is required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ title: '', commitmentType: 'STABILITY_DATA', authority: 'SFDA' });
      navigate({ to: '/regulatory/commitments/$id', params: { id: created.commitmentId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not record the commitment.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Post-Approval Commitments"
        subtitle="Conditions and commitments made to the authority at approval — stability data, post-market surveillance, labelling updates — tracked to fulfilment."
        actions={<button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>Record commitment</button>}
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open</span></div>
          <div className={`${styles.kpi} ${stats.overdue > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span></div>
          <div className={`${styles.kpi} ${stats.due_soon > 0 ? styles.kpiWarn : ''}`}><span className={styles.kpiNum}>{stats.due_soon}</span><span className={styles.kpiLabel}>Due ≤ 60 days</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.fulfilled}</span><span className={styles.kpiLabel}>Fulfilled</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(COMMITMENT_STATUS_LABEL).map((s) => <option key={s} value={s}>{COMMITMENT_STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Type filter" value={type} onChange={(e) => setType(e.target.value)} className={styles.select}>
          <option value="">All types</option>
          {Object.keys(COMMITMENT_TYPE_LABEL).map((tp) => <option key={tp} value={tp}>{COMMITMENT_TYPE_LABEL[tp]}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} commitment{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading commitments…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No commitments match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead><tr><th>Commitment</th><th>Title</th><th>Type</th><th>Registration</th><th>Status</th><th>Due</th></tr></thead>
          <tbody>
            {items.map((c) => {
              const overdue = c.dueDate && new Date(c.dueDate).getTime() < today && c.status !== 'FULFILLED' && c.status !== 'CANCELLED';
              return (
                <tr key={c.commitmentId} className={styles.row} onClick={() => navigate({ to: '/regulatory/commitments/$id', params: { id: c.commitmentId } })}>
                  <td className={styles.mono}>{c.commitmentNumber}</td>
                  <td>{c.title}</td>
                  <td>{COMMITMENT_TYPE_LABEL[c.commitmentType] ?? c.commitmentType}</td>
                  <td>{c.registrationCode ?? <span className={styles.sub}>—</span>}</td>
                  <td><span className={`${styles.pill} ${statusClass(c.status)}`}>{COMMITMENT_STATUS_LABEL[c.status] ?? c.status}</span></td>
                  <td>{fmtDate(c.dueDate)}{overdue && <span className={styles.sub} style={{ color: 'var(--color-danger-text, #b42318)' }}> · overdue</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Record commitment">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Record a commitment</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Title *</span>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Submit 12-month stability data" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Type</span>
                <select value={form.commitmentType} onChange={(e) => setForm({ ...form, commitmentType: e.target.value })}>
                  {Object.keys(COMMITMENT_TYPE_LABEL).map((tp) => <option key={tp} value={tp}>{COMMITMENT_TYPE_LABEL[tp]}</option>)}
                </select></label>
              <label className={styles.field}><span>Authority</span>
                <input type="text" value={form.authority ?? ''} onChange={(e) => setForm({ ...form, authority: e.target.value })} /></label>
            </div>
            <label className={styles.field}><span>Due date</span>
              <input type="date" value={form.dueDate ?? ''} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></label>
            <label className={styles.field}><span>Description</span>
              <textarea rows={3} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Recording…' : 'Record commitment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
